import React from 'react';
import { Tag as AntdTag } from 'antd';
import { css } from '@emotion/react';
import styled from 'types/theme.types';
import type { TagProps as AntdTagProps } from 'antd/lib/tag';

export const TAG_PRESETS = ['gray', 'blue', 'cyan', 'green', 'lime', 'orange', 'purple', 'red'] as const;

export type TagPreset = (typeof TAG_PRESETS)[number];
export type TagPalette = { text: string; background: string; border: string };

// Gray is the neutral chip (the tags preview strip, untagged states), so it stays out of the rotation.
const HASHED_PRESETS = TAG_PRESETS.filter(preset => preset !== 'gray');

/**
 * One stable colour per seed, so a tag key reads as one family wherever its values are listed
 * (environment-level-tags tech design §3). `| 0` keeps the running hash in int32 - past 2^53 the
 * arithmetic would stop being exact and the same key could drift to another colour.
 */
export const tagPresetByHash = (seed: string): TagPreset => {
  const hash = [...seed].reduce((acc, char) => (acc * 31 + char.charCodeAt(0)) | 0, 0);

  return HASHED_PRESETS[Math.abs(hash) % HASHED_PRESETS.length];
};

type TagTokenSlot = 'text' | 'background' | 'border' | 'icon' | 'text-hover' | 'border-hover' | 'icon-hover';

export const tagToken = (preset: TagPreset, slot: TagTokenSlot): string =>
  `var(--component-tag-color-${preset}-${slot})`;

// tokens.dark.css redeclares every --component-tag-color-* under [data-theme='dark'], so both modes
// resolve from the same string and nothing reads theme.mode.
const TOKEN_PALETTE = Object.fromEntries(
  TAG_PRESETS.map(preset => [
    preset,
    { text: tagToken(preset, 'text'), background: tagToken(preset, 'background'), border: tagToken(preset, 'border') }
  ])
) as Record<TagPreset, TagPalette>;

export const tagPalette = (preset: TagPreset): TagPalette => TOKEN_PALETTE[preset];

// A preset paints the tag from its tokens, so it wins over antd's own `color`.
export type TagProps = AntdTagProps & { preset?: TagPreset };

export const Tag: React.FunctionComponent<TagProps> = ({ children, preset, color, ...props }) => (
  <StyledTag {...props} preset={preset} color={preset ? undefined : color}>
    {children}
  </StyledTag>
);

const StyledTag = styled(AntdTag, { shouldForwardProp: prop => prop !== 'preset' })<{ preset?: TagPreset }>`
  margin-inline-end: 0;
  text-align: center;
  /* A call site that puts a tag inline with text needs its own vertical-align: overflow moves an
     inline-block's baseline to the bottom margin edge, so the tag rides above the text's centre. */
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  ${({ preset, bordered }) => {
    if (!preset) return undefined;

    const palette = tagPalette(preset);

    return css`
      color: ${palette.text};
      background: ${palette.background};
      border-color: ${bordered === false ? 'transparent' : palette.border};
    `;
  }}

  ${props =>
    props.preset &&
    css`
      .ant-tag-close-icon {
        color: ${props.preset === 'gray' ? tagToken('gray', 'icon') : tagToken(props.preset, 'text')};
      }
    `}

  /* Gray's hover is an affordance, so it paints only where there is something to click - on a static
     chip it reads as a link it is not (ENG-2787). */
  ${props =>
    props.preset === 'gray' &&
    Boolean(props.closable || props.onClick) &&
    css`
      &:hover {
        color: ${tagToken('gray', 'text-hover')};
        border-color: ${props.bordered === false ? 'transparent' : tagToken('gray', 'border-hover')};

        .ant-tag-close-icon {
          color: ${tagToken('gray', 'icon-hover')};
        }
      }
    `}
`;

/**
 * A chip for an antd select's `tagRender`, coloured from `seed` - pass the tag key, so one key's
 * chips match wherever they are listed. A component rather than a preset because of the two things
 * antd stops doing once `tagRender` takes over:
 *
 * - it no longer guards the selector's mousedown, so clicking a chip's close icon would reopen the
 *   dropdown on the way out;
 * - it no longer puts a `title` on the chip, which is the only way to read a chip the selector has
 *   ellipsised - hence `title` being required rather than optional here.
 */
export const HashedChip: React.FC<Omit<TagProps, 'preset'> & { seed: string; title: string }> = ({
  seed,
  ...props
}) => <SelectChip preset={tagPresetByHash(seed)} onMouseDown={event => event.preventDefault()} {...props} />;

// antd spaces its own chips through .ant-select-selection-item, which tagRender replaces. rc-select
// wraps the chip in a block span, so the chip needs the vertical-align the overflow rule above costs it.
const SelectChip = styled(Tag)`
  margin-inline-end: 4px;
  vertical-align: middle;
`;
